// theConsent banner runtime · v2 (Path B — static, DOM-driven)
// © theCSS Agency — https://gettheconsent.com
//
// The banner UI is authored as Webflow elements. This script is a SINGLE static
// hosted file (identical bytes for every site). It reads `data-tc-*` config from
// the DOM, manages consent, drives Google Consent Mode v2, persists the choice
// (cookie + localStorage), and re-activates user-tagged gated scripts.
//
// It does NOT: fetch config or a blocklist, load remote code, override
// document.createElement / the script src setter, inject HTML from remote data,
// or accept postMessage on published sites.

import { findBanner, findPreferences, readConfig } from './dom-config';
import { loadConsent, saveConsent, clearConsent } from './storage';
import { emitDefaults, emitUpdate } from './gcm';
import { gpcEnabled } from './gpc';
import { activateGatedScripts } from './gating';
import type { Category, ConsentMap, ConsentRecord, RuntimeConfig } from './types';

declare const __VERSION__: string;

interface RuntimeAPI {
  version: string;
  show(): void;
  showPreferences(): void;
  acceptAll(): void;
  rejectAll(): void;
  getConsent(): ConsentMap | null;
  reset(): void;
  on(event: 'change', cb: (consent: ConsentMap) => void): () => void;
}

declare global {
  interface Window {
    theConsentAPI?: RuntimeAPI;
  }
}

(function bootstrap(): void {
  // Hide the consent elements BEFORE first paint to prevent a flash of all
  // popups on load. This is a header script that runs before <body> is parsed,
  // and the hide rule uses attribute selectors, so injecting it now means the
  // elements are hidden the instant they render. (Doing this at DOMContentLoaded
  // caused the flash: the body painted with everything visible, then JS hid it.)
  injectBaseStyle();

  // GCM v2 safe baseline — emitted SYNCHRONOUSLY from this header script so no
  // Google tags fire before the visitor decides. The banner UI lives in <body>,
  // which isn't parsed yet when a header script runs, so everything that needs
  // the DOM (finding the banner, reading its data-tc-* config, rendering) waits
  // for DOMContentLoaded.
  emitDefaults(deniedDefaults());

  ready(() => {
    const banner = findBanner();
    if (!banner) {
      // No consent UI on this page (component not placed here) → don't strand
      // the page's own tags in the denied baseline; release them.
      emitUpdate(grantedDefaults());
      return;
    }

    const cfg = readConfig(banner);

    // ── License gate ──────────────────────────────────────────────────────────
    // Free to build & test on Webflow staging (.webflow.io) / local hosts; a
    // live/custom domain needs a paid license. Trackers stay denied (the
    // baseline above) while we verify, and we fail OPEN on any network error so
    // our outage never breaks a customer's site. No siteKey → never gate.
    if (cfg.siteKey && isLiveHost(location.hostname)) {
      verifyLicensed(cfg.siteKey).then((ok) => {
        if (ok) {
          run(banner, cfg);
        } else {
          console.warn(
            '[theConsent] A paid license is required to run on ' +
              location.hostname +
              '. The banner is inactive on this domain. Get a license at https://www.gettheconsent.com',
          );
        }
      });
      return;
    }

    run(banner, cfg);
  });
})();

function run(banner: HTMLElement, cfg: RuntimeConfig): void {
  const prefs = findPreferences();
  const listeners = new Set<(c: ConsentMap) => void>();

  const allDenied = (): ConsentMap => map((c) => c === 'necessary');
  const allGranted = (): ConsentMap => map(() => true);
  function map(fn: (c: Category) => boolean): ConsentMap {
    const out = {} as ConsentMap;
    (['necessary', 'preferences', 'statistics', 'marketing'] as Category[]).forEach((c) => {
      out[c] = c === 'necessary' ? true : fn(c);
    });
    return out;
  }

  // Initial consent: stored choice wins; otherwise the type's default.
  // opt-in (GDPR) defaults to denied; opt-out / info default to granted.
  const stored = loadConsent(cfg);
  const typeDefault = cfg.type === 'opt-in' ? allDenied() : allGranted();
  let consent: ConsentMap = stored ? normalize(stored.consent) : typeDefault;

  // GPC: honor an explicit browser opt-out when there's no prior choice.
  let gpcApplied = false;
  if (!stored && cfg.gpc && gpcEnabled()) {
    consent = allDenied();
    gpcApplied = true;
  }

  // GCM v2: the denied baseline was already set synchronously at bootstrap.
  // Now reflect the resolved initial state with an UPDATE (never a second
  // `default`, which would conflict). opt-in with no prior choice stays denied
  // to match the baseline, so tags wait until the visitor accepts.
  if (cfg.gcm) {
    if (stored || gpcApplied || cfg.type !== 'opt-in') emitUpdate(consent);
  } else {
    // GCM management disabled for this site — release the denied baseline so we
    // don't block the site's own tags.
    emitUpdate(allGranted());
  }

  const finalize = (next: ConsentMap, persist: boolean): void => {
    consent = normalize(next);
    if (persist) {
      const now = Date.now();
      const record: ConsentRecord = {
        v: 2,
        ts: now,
        exp: now + cfg.expiryDays * 86400 * 1000,
        ver: cfg.version,
        consent,
      };
      saveConsent(cfg, record);
    }
    if (cfg.gcm) emitUpdate(consent);
    activateGatedScripts(consent);
    closeBanner();
    closePrefs();
    showReopen();
    listeners.forEach((l) => {
      try {
        l(consent);
      } catch {}
    });
    sendBeacon(consent, cfg);
  };

  // ── visibility helpers ────────────────────────────────────────────────────
  function openBanner(): void {
    hideReopen();
    closePrefs();
    show(banner!);
  }
  function closeBanner(): void {
    hide(banner!);
  }
  function openPrefs(): void {
    hideReopen();
    if (!prefs) {
      // No separate preferences panel authored — the banner itself is the panel.
      syncToggles(banner!, consent);
      show(banner!);
      return;
    }
    syncToggles(prefs, consent);
    show(prefs);
  }
  function closePrefs(): void {
    if (prefs) hide(prefs);
  }
  function showReopen(): void {
    eachReopen((el) => show(el));
  }
  function hideReopen(): void {
    eachReopen((el) => hide(el));
  }

  // ── wire up actions ───────────────────────────────────────────────────────
  bindActions(document, {
    onAcceptAll: () => finalize(allGranted(), true),
    onRejectAll: () => finalize(allDenied(), true),
    onSave: () => finalize(readToggles(prefs || banner!, consent), true),
    onPreferences: openPrefs,
    onClose: () => {
      // Dismiss without recording → fall back to the reopen control; the banner
      // returns on the next page load until a real choice is made.
      closePrefs();
      closeBanner();
      showReopen();
    },
  });
  eachReopen((el) => on(el, 'click', openPrefs));

  // ── initial render decision ───────────────────────────────────────────────
  ready(() => {
    if (stored || gpcApplied) {
      if (gpcApplied) {
        const now = Date.now();
        saveConsent(cfg, {
          v: 2,
          ts: now,
          exp: now + cfg.expiryDays * 86400 * 1000,
          ver: cfg.version,
          consent,
        });
      }
      activateGatedScripts(consent);
      closeBanner();
      closePrefs();
      showReopen();
    } else {
      openBanner();
    }
  });

  // ── public API ────────────────────────────────────────────────────────────
  const api: RuntimeAPI = {
    version: typeof __VERSION__ === 'string' ? __VERSION__ : '2',
    show: openBanner,
    showPreferences: openPrefs,
    acceptAll: () => finalize(allGranted(), true),
    rejectAll: () => finalize(allDenied(), true),
    getConsent: () => (loadConsent(cfg) ? { ...consent } : null),
    reset: () => {
      clearConsent(cfg);
      consent = typeDefault;
      if (cfg.gcm) emitUpdate(consent);
      hideReopen();
      openBanner();
    },
    on: (event, cb) => {
      if (event === 'change') listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
  window.theConsentAPI = api;
}

// ── license gate helpers ─────────────────────────────────────────────────────

/** Is this a live/custom domain (vs Webflow staging or local dev)? */
function isLiveHost(host: string): boolean {
  const h = host.toLowerCase();
  return !(
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '[::1]' ||
    h.endsWith('.localhost') ||
    h.endsWith('.webflow.io')
  );
}

/** All categories denied except necessary — the safe default while verifying. */
function deniedDefaults(): ConsentMap {
  return { necessary: true, preferences: false, statistics: false, marketing: false };
}

/** All categories granted — used to release the denied baseline on pages that
 *  have no banner (nothing to consent with) or when GCM is disabled. */
function grantedDefaults(): ConsentMap {
  return { necessary: true, preferences: true, statistics: true, marketing: true };
}

/**
 * Ask the API whether this site holds a paid license. Resolves true for paid
 * plans, false for free/draft, and — deliberately — true on ANY error so an
 * outage on our side never disables a paying customer's banner.
 */
function verifyLicensed(siteKey: string): Promise<boolean> {
  const url =
    'https://www.gettheconsent.com/api/v1/entitlement?siteKey=' + encodeURIComponent(siteKey);
  return fetch(url, { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : null))
    .then((d: { plan?: string } | null) => {
      if (!d || !d.plan) return true; // fail open
      return d.plan === 'starter' || d.plan === 'pro' || d.plan === 'agency';
    })
    .catch(() => true); // fail open — never break the site on our outage
}

// ── helpers (module scope) ───────────────────────────────────────────────────

function normalize(input: Partial<ConsentMap>): ConsentMap {
  const out = {} as ConsentMap;
  (['necessary', 'preferences', 'statistics', 'marketing'] as Category[]).forEach((c) => {
    out[c] = c === 'necessary' ? true : !!input[c];
  });
  return out;
}

interface ActionHandlers {
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSave: () => void;
  onPreferences: () => void;
  onClose: () => void;
}

function bindActions(root: Document, h: ActionHandlers): void {
  // Single delegated listener so dynamically-revealed elements (e.g. a prefs
  // modal toggled by Webflow interactions) still work without re-binding.
  root.addEventListener(
    'click',
    (ev) => {
      const target = ev.target as Element | null;
      const el = target && target.closest ? target.closest('[data-tc-action]') : null;
      if (!el) return;
      const action = el.getAttribute('data-tc-action');
      if (!action) return;
      ev.preventDefault();
      switch (action) {
        case 'accept-all':
          return h.onAcceptAll();
        case 'reject-all':
          return h.onRejectAll();
        case 'save':
          return h.onSave();
        case 'preferences':
          return h.onPreferences();
        case 'close':
          return h.onClose();
      }
    },
    false,
  );
}

/** Read the current state of `[data-tc-category]` checkbox inputs. */
function readToggles(scope: HTMLElement, fallback: ConsentMap): ConsentMap {
  const out = normalize(fallback);
  try {
    const nodes = scope.querySelectorAll('[data-tc-category]');
    nodes.forEach((n) => {
      const c = (n as HTMLElement).getAttribute('data-tc-category') as Category | null;
      if (!c || c === 'necessary') return;
      const input = n as HTMLInputElement;
      if (typeof input.checked === 'boolean') out[c] = input.checked;
    });
  } catch {}
  return out;
}

/** Reflect the given consent into the `[data-tc-category]` toggles. */
function syncToggles(scope: HTMLElement, consent: ConsentMap): void {
  try {
    const nodes = scope.querySelectorAll('[data-tc-category]');
    nodes.forEach((n) => {
      const c = (n as HTMLElement).getAttribute('data-tc-category') as Category | null;
      if (!c) return;
      const input = n as HTMLInputElement;
      if (typeof input.checked === 'boolean') {
        input.checked = c === 'necessary' ? true : !!consent[c];
        if (c === 'necessary') input.disabled = true;
      }
    });
  } catch {}
}

function eachReopen(fn: (el: HTMLElement) => void): void {
  try {
    document.querySelectorAll('[data-tc-reopen]').forEach((n) => fn(n as HTMLElement));
  } catch {}
}

function show(el: HTMLElement): void {
  // Inline style overrides the injected `display:none` base rule. We use the
  // element's authored display (data-tc-display) so the user's Webflow layout
  // is preserved; default to flex (typical for a banner bar/card).
  el.style.display = el.getAttribute('data-tc-display') || 'flex';
}
function hide(el: HTMLElement): void {
  el.style.display = 'none';
}

function on(el: Element, type: string, fn: () => void): void {
  el.addEventListener(type, (e) => {
    e.preventDefault();
    fn();
  });
}

function ready(fn: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

/** Static base stylesheet — hides the consent elements until the runtime shows
 *  them. Pure CSS, no config-driven values. */
function injectBaseStyle(): void {
  if (document.getElementById('tc-base-style')) return;
  const style = document.createElement('style');
  style.id = 'tc-base-style';
  style.textContent =
    '[data-tc-banner],[data-tc-preferences],[data-tc-reopen]{display:none}';
  (document.head || document.documentElement).appendChild(style);
}

/** Fire a data-only consent log beacon. Never blocks; failures are ignored. */
function sendBeacon(consent: ConsentMap, cfg: RuntimeConfig): void {
  try {
    const apiBase = 'https://www.gettheconsent.com';
    const body = JSON.stringify({
      consent,
      type: cfg.type,
      ver: cfg.version,
      gpc: gpcEnabled(),
      url: location.hostname,
    });
    const url = `${apiBase}/api/v1/consent`;
    if (typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        credentials: 'omit',
        keepalive: true,
      }).catch(() => {});
    }
  } catch {}
}
